import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@gastrosoft/shared/ui';
import { GetLicencesDocument, type GetLicencesQuery } from '@/generated/root-types';
import { licenceStateColors, translateLicenceState } from '@/utils/billingStateLabels';

interface LicenceRow {
  id: string;
  tenantId?: string;
  serial: string;
  appName: string;
  state: string;
  type: string;
  expirationDate?: string | null;
  createdAt: string;
  tenantName?: string;
  orgUnitName?: string;
  [key: string]: unknown;
}

export default function Licences() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading } = useQuery<GetLicencesQuery>(GetLicencesDocument, {
    variables: { first: 50 },
  });

  const licences = data?.licences?.nodes ?? [];
  const totalCount = data?.licences?.totalCount ?? 0;

  const rows: LicenceRow[] = licences.map((l) => ({
    id: l.id,
    tenantId: l.tenantId,
    serial: l.serial,
    appName: l.appName,
    state: l.state,
    type: l.type,
    expirationDate: l.expirationDate,
    createdAt: l.createdAt,
    tenantName: l.tenant?.name ?? '–',
    orgUnitName: l.organizationUnit?.name ?? '–',
  }));

  const columns: GridColDef[] = [
    { field: 'tenantName', headerName: t('common.tenant'), width: 160 },
    { field: 'orgUnitName', headerName: t('licences.orgUnit'), width: 140 },
    { field: 'serial', headerName: t('common.serialNumber'), flex: 1, minWidth: 200 },
    { field: 'appName', headerName: t('common.app'), width: 150 },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 110,
      renderCell: (params) => (
        <Chip
          label={translateLicenceState(t, String(params.value))}
          size="small"
          color={licenceStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    { field: 'type', headerName: t('common.type'), width: 120 },
    {
      field: 'expirationDate',
      headerName: t('common.validUntil'),
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('licences.title')}
      </Typography>
      <DataTable<LicenceRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={(row) => navigate(`/licences/${row.id}`)}
        paginationMode="client"
      />
    </Box>
  );
}
